import React, { type JSX } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

/** Shown when the YAML itself could not be parsed. */
export function YamlParseError({ message }: { message: string }): JSX.Element {
  return (
    <div className="h-full overflow-auto p-6 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">Invalid YAML Format</h3>
              <p className="text-sm text-red-700 mb-3">
                The YAML file could not be parsed. Please check the file format.
              </p>
              <pre className="text-xs text-red-600 bg-red-100 p-3 rounded font-mono overflow-x-auto">
                {message}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Parsed, but without the paragraphs structure — fall back to raw YAML. */
export function YamlRawFallback({ contentText }: { contentText: string }): JSX.Element {
  return (
    <div className="h-full overflow-auto p-6 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-yellow-900 mb-1">
                No Structured Preview Available
              </h4>
              <p className="text-xs text-yellow-700">
                This YAML file doesn&apos;t have the expected &quot;paragraphs&quot; structure for
                rich preview. Showing raw content below.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <pre className="text-xs text-gray-700 font-mono leading-relaxed whitespace-pre-wrap">
            {contentText}
          </pre>
        </div>
      </div>
    </div>
  );
}
